import { useCallback, useEffect, useState } from 'react';
import { LuCopy, LuPencil, LuRefreshCw, LuTrash2 } from 'react-icons/lu';
import aviso from '../../lib/aviso';
import RecordPanel, { PanelSection } from '../RecordPanel';
import Select from '../Select';
import Dialog from '../Dialog';
import {
    WebhookEntradaService, type WebhookEntrada, type WebhookEntradaCampo, type WebhookEntradaConfig,
} from '../../services/WebhookEntradaService';
import { formatarData } from '../conexoes/canais';
import './WebhookEntradaPanel.css';

/**
 * O painel de uma entrada do webhook em Conexões (docs/designsystem/propostas/webhook-de-entrada.html).
 * Duas abas: Entrada (o endereço, o que chegou e o que fazer com quem chega) e Como enviar
 * (o formato, as chaves dos campos e o passo a passo do Make e do n8n). Criar não mora aqui:
 * é a porta da tela Apps. O endereço leva a chave secreta — só o administrador o vê.
 * Prefixo `whe-`.
 */

interface Props {
    entradaId: number | null;
    onClose: () => void;
    admin: boolean;
    usuarios: { id: number; name: string }[];
    /** A entrada mudou ou foi apagada: a lista de Conexões recarrega. */
    onMudou: () => void;
}

type Aba = 'entrada' | 'enviar';

const DISTRIBUIR = '__distribuir__';

const TIPOS: Record<number, string> = { 1: 'texto', 2: 'número', 3: 'data', 4: 'sim/não', 5: 'seleção' };

const relativo = (iso: string | null): string => {
    if (!iso) return 'nenhum ainda';
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (Number.isNaN(min)) return 'nenhum ainda';
    if (min < 1) return 'agora mesmo';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    return d === 1 ? 'há 1 dia' : `há ${d} dias`;
};

/** Um valor de exemplo que o campo aceita, para o JSON de exemplo já sair válido. */
const exemploDoCampo = (c: WebhookEntradaCampo): string | number | boolean => {
    switch (c.tipo) {
        case 2: return 30;
        case 3: return '2026-10-15';
        case 4: return true;
        case 5: return c.opcoes?.[0] ?? 'opção';
        default: return 'texto';
    }
};

const montarExemplo = (campos: WebhookEntradaCampo[]): string => {
    const corpo: Record<string, unknown> = {
        nome: 'Ana Souza',
        telefone: '(41) 99999-0001',
        email: 'ana@exemplo.com',
        id_externo: 'form-8812',
        observacao: 'Quer orçamento',
    };
    if (campos.length) corpo.campos = Object.fromEntries(campos.slice(0, 2).map(c => [c.chave, exemploDoCampo(c)]));
    return JSON.stringify(corpo, null, 2);
};

const copiar = async (texto: string, ok: string) => {
    try { await navigator.clipboard.writeText(texto); aviso.ok(ok); }
    catch { aviso.erro('Não foi possível copiar. Selecione o texto e copie à mão.'); }
};

const WebhookEntradaPanel = ({ entradaId, onClose, admin, usuarios, onMudou }: Props) => {
    const open = entradaId !== null;
    const [aba, setAba] = useState<Aba>('entrada');
    const [entrada, setEntrada] = useState<WebhookEntrada | null>(null);
    const [campos, setCampos] = useState<WebhookEntradaCampo[]>([]);
    const [url, setUrl] = useState<string | null>(null);
    const [editando, setEditando] = useState(false);
    const [nome, setNome] = useState('');
    const [config, setConfig] = useState<WebhookEntradaConfig | null>(null);
    const [erroNome, setErroNome] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [confirmar, setConfirmar] = useState<'chave' | 'apagar' | null>(null);

    const aplicar = (e: WebhookEntrada) => {
        setEntrada(e);
        setNome(e.nome);
        setConfig(e.entrada);
        setErroNome('');
    };

    const carregar = useCallback(async (id: number) => {
        const [res, end] = await Promise.all([
            WebhookEntradaService.obter(id),
            admin ? WebhookEntradaService.endereco(id) : Promise.resolve(null),
        ]);
        if (!res.sucesso || !res.dados) { aviso.erro(res.mensagem || 'Não foi possível ler a entrada.'); onClose(); return; }
        aplicar(res.dados.entrada);
        setCampos(res.dados.campos);
        setUrl(end?.sucesso && end.dados ? end.dados.url : null);
    }, [admin, onClose]);

    useEffect(() => {
        if (entradaId === null) return;
        setAba('entrada');
        setEditando(false);
        setEntrada(null);
        setUrl(null);
        carregar(entradaId);
    }, [entradaId, carregar]);

    const cancelar = () => { if (entrada) aplicar(entrada); setEditando(false); };

    const salvar = async () => {
        if (!entrada || !config) return;
        if (!nome.trim()) { setErroNome('Dê um nome para a entrada, como "Formulário do site".'); return; }
        if (config.responsavel === 'usuario' && !config.usuario_id) {
            aviso.atencao('Escolha quem fica responsável pelas oportunidades desta entrada.');
            return;
        }
        setOcupado(true);
        const res = await WebhookEntradaService.salvar(entrada.id, nome.trim(), config);
        setOcupado(false);
        if (!res.sucesso || !res.dados) {
            if (/nome|chamada/i.test(res.mensagem)) setErroNome(res.mensagem);
            else aviso.erro(res.mensagem || 'Não foi possível salvar.');
            return;
        }
        aplicar(res.dados);
        setEditando(false);
        aviso.ok('Entrada salva.');
        onMudou();
    };

    const gerarNovaChave = async () => {
        if (!entrada) return;
        setOcupado(true);
        const res = await WebhookEntradaService.novaChave(entrada.id);
        setOcupado(false);
        setConfirmar(null);
        if (!res.sucesso || !res.dados) { aviso.erro(res.mensagem || 'Não foi possível gerar o endereço novo.'); return; }
        setUrl(res.dados.url);
        aviso.ok('Endereço novo gerado. Atualize a ferramenta que envia.');
    };

    const apagar = async () => {
        if (!entrada) return;
        setOcupado(true);
        const res = await WebhookEntradaService.excluir(entrada.id);
        setOcupado(false);
        setConfirmar(null);
        if (!res.sucesso) { aviso.erro(res.mensagem || 'Não foi possível apagar a entrada.'); return; }
        aviso.ok(`${entrada.nome} apagada.`);
        onMudou();
        onClose();
    };

    const nomeDoUsuario = (id: number | null) => usuarios.find(u => u.id === id)?.name ?? `Usuário ${id}`;
    const travado = !admin || ocupado || !editando;
    const botaoEditar = admin && !editando && (
        <button type="button" className="rp-btn" onClick={() => setEditando(true)}>
            <LuPencil size={13} /> Editar
        </button>
    );

    return (
        <>
            <RecordPanel
                open={open}
                onClose={onClose}
                initials="WH"
                squareAvatar
                title={entrada?.nome ?? 'Webhook de entrada'}
                subtitle={entrada ? `Webhook de entrada · criada em ${formatarData(entrada.criadaEm)}` : 'Carregando…'}
                tabs={[
                    { id: 'entrada', label: 'Entrada' },
                    { id: 'enviar', label: 'Como enviar' },
                ]}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
                editing={editando}
                onBack={cancelar}
                footer={
                    <>
                        <button type="button" className="rp-btn" onClick={cancelar} disabled={ocupado}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={ocupado}>
                            {ocupado ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {!entrada || !config ? (
                    <div className="lk-skeleton" style={{ height: 120 }} />
                ) : aba === 'entrada' ? (
                    <>
                        <PanelSection title="Endereço" extra={!editando && admin ? (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmar('apagar')} disabled={ocupado}>
                                <LuTrash2 size={13} /> Apagar
                            </button>
                        ) : undefined}>
                            {admin && url ? (
                                <>
                                    <div className="whe-url cx-mono">{url}</div>
                                    <div className="whe-acoes">
                                        <button type="button" className="rp-btn" onClick={() => copiar(url, 'Endereço copiado.')}>
                                            <LuCopy size={13} /> Copiar
                                        </button>
                                        <button type="button" className="rp-btn" onClick={() => setConfirmar('chave')} disabled={ocupado}>
                                            <LuRefreshCw size={13} /> Gerar novo endereço
                                        </button>
                                    </div>
                                    <p className="rp-hint">Cole este endereço na ferramenta que envia os leads. Ele é a senha da entrada: quem tiver o endereço consegue mandar lead.</p>
                                </>
                            ) : (
                                <p className="rp-hint">O endereço é a senha da entrada: só o administrador da empresa vê.</p>
                            )}
                        </PanelSection>

                        <PanelSection title="O que chegou">
                            <div className="rp-kv cx-kv">
                                <span className="rp-k">Último lead</span><span className="rp-v">{relativo(entrada.ultimoEvento)}</span>
                                <span className="rp-k">Recebidos hoje</span>
                                <span className="rp-v">
                                    {entrada.recebidosHoje} {entrada.recebidosHoje === 1 ? 'lead' : 'leads'} · {entrada.oportunidadesHoje} {entrada.oportunidadesHoje === 1 ? 'oportunidade' : 'oportunidades'}
                                </span>
                                {entrada.ultimaRecusa && (
                                    <>
                                        <span className="rp-k">Última recusa</span>
                                        <span className="rp-v cx-warn">{relativo(entrada.ultimaRecusa.em)} · {entrada.ultimaRecusa.motivo}</span>
                                    </>
                                )}
                            </div>
                        </PanelSection>

                        <PanelSection title="O que fazer com quem chega" extra={botaoEditar || undefined}>
                            {!editando ? (
                                <div className="rp-kv cx-kv">
                                    <span className="rp-k">Nome</span><span className="rp-v">{entrada.nome}</span>
                                    <span className="rp-k">Quem chega</span>
                                    <span className="rp-v">{config.acao === 'oportunidade' ? 'Criar contato e abrir oportunidade' : 'Só criar o contato'}</span>
                                    {config.acao === 'oportunidade' && (
                                        <>
                                            <span className="rp-k">Etapa de entrada</span><span className="rp-v">Não iniciado</span>
                                            <span className="rp-k">Responsável</span>
                                            <span className="rp-v">{config.responsavel === 'usuario' && config.usuario_id ? nomeDoUsuario(config.usuario_id) : 'Distribuir entre quem recebe lead'}</span>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="rp-field">
                                        <label className="rp-label" htmlFor="whe-nome">Nome</label>
                                        <input id="whe-nome" className="rp-input" value={nome} maxLength={80} disabled={travado}
                                            aria-invalid={!!erroNome}
                                            onChange={e => { setNome(e.target.value); setErroNome(''); }} />
                                        {erroNome
                                            ? <p className="rp-error">{erroNome}</p>
                                            : <p className="rp-hint">É a origem que aparece na oportunidade.</p>}
                                    </div>
                                    <div className="rp-field">
                                        <label className="rp-label" htmlFor="whe-acao">Ao receber um lead</label>
                                        <Select id="whe-acao" disabled={travado} value={config.acao}
                                            options={[
                                                { value: 'oportunidade', label: 'Criar contato e abrir oportunidade' },
                                                { value: 'contato', label: 'Só criar o contato' },
                                            ]}
                                            onChange={v => setConfig(x => x && ({ ...x, acao: v as WebhookEntradaConfig['acao'] }))} />
                                    </div>
                                    {config.acao === 'oportunidade' && (
                                        <>
                                            <div className="rp-field">
                                                <span className="rp-label">Etapa de entrada</span>
                                                <span className="rp-v">Não iniciado</span>
                                            </div>
                                            <div className="rp-field">
                                                <label className="rp-label" htmlFor="whe-resp">Responsável</label>
                                                <Select id="whe-resp" disabled={travado}
                                                    value={config.responsavel === 'usuario' && config.usuario_id ? String(config.usuario_id) : DISTRIBUIR}
                                                    options={[
                                                        { value: DISTRIBUIR, label: 'Distribuir entre quem recebe lead' },
                                                        ...usuarios.map(u => ({ value: String(u.id), label: u.name })),
                                                    ]}
                                                    onChange={v => setConfig(x => x && (v === DISTRIBUIR
                                                        ? { ...x, responsavel: 'distribuir', usuario_id: null }
                                                        : { ...x, responsavel: 'usuario', usuario_id: Number(v) }))} />
                                                <p className="rp-hint">Distribuir usa a mesma régua das conversas novas: quem recebe lead e tem menos conversas abertas.</p>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                            <p className="rp-hint">
                                A pessoa é achada pelo telefone e, sem telefone, pelo e-mail. A oportunidade aparece na Pipeline, mas não no Chat — não há conversa para responder.
                            </p>
                        </PanelSection>
                    </>
                ) : (
                    <>
                        <PanelSection title="O formato" extra={
                            <button type="button" className="rp-btn" onClick={() => copiar(montarExemplo(campos), 'Exemplo copiado.')}>
                                <LuCopy size={13} /> Copiar exemplo
                            </button>
                        }>
                            <pre className="whe-codigo">{montarExemplo(campos)}</pre>
                            <p className="rp-hint">
                                POST com <span className="cx-mono">Content-Type: application/json</span>. Telefone <strong>ou</strong> e-mail é
                                obrigatório; o resto é opcional. O mesmo <span className="cx-mono">id_externo</span> não vira lead duas vezes.
                                A <span className="cx-mono">observacao</span> entra como detalhe da oportunidade.
                            </p>
                        </PanelSection>

                        <PanelSection title="Campos personalizados">
                            {campos.length === 0 ? (
                                <p className="rp-hint">A empresa ainda não tem campos personalizados. Crie em Configurações › Campos personalizados e use a chave dentro de <span className="cx-mono">campos</span>.</p>
                            ) : (
                                <>
                                    <div className="rp-kv cx-kv">
                                        {campos.map(c => (
                                            <span key={c.chave} style={{ display: 'contents' }}>
                                                <span className="rp-k cx-mono">{c.chave}</span>
                                                <span className="rp-v">
                                                    {c.nome} <span className="lk-muted">· {TIPOS[c.tipo] ?? 'texto'}{c.opcoes?.length ? `: ${c.opcoes.join(', ')}` : ''}</span>
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                    <p className="rp-hint">Chave que não existe ou valor que o campo não aceita não recusa o lead: o campo fica de fora e a resposta traz o aviso.</p>
                                </>
                            )}
                        </PanelSection>

                        <PanelSection title="No Make">
                            <ol className="whe-passos">
                                <li>Depois do gatilho (o formulário, a planilha…), adicione o módulo <strong>HTTP › Make a request</strong>.</li>
                                <li>URL: o endereço da aba Entrada. Method: <strong>POST</strong>.</li>
                                <li>Body type: <strong>Raw</strong>, Content type: <strong>JSON (application/json)</strong>.</li>
                                <li>Em Request content, monte o JSON do formato acima com os campos do gatilho.</li>
                            </ol>
                        </PanelSection>

                        <PanelSection title="No n8n">
                            <ol className="whe-passos">
                                <li>Adicione o nó <strong>HTTP Request</strong> depois do gatilho.</li>
                                <li>Method: <strong>POST</strong>. URL: o endereço da aba Entrada.</li>
                                <li>Ligue <strong>Send Body</strong>, Body Content Type: <strong>JSON</strong>, e monte o corpo do formato acima.</li>
                            </ol>
                            <p className="rp-hint">
                                Resposta 200 = lead recebido. 400 traz o motivo (o mesmo que aparece em "Última recusa"); 404 = endereço errado ou trocado.
                            </p>
                        </PanelSection>
                    </>
                )}
            </RecordPanel>

            <Dialog
                open={confirmar === 'chave'}
                onClose={() => setConfirmar(null)}
                title={`Gerar um endereço novo para ${entrada?.nome ?? 'esta entrada'}?`}
                confirmLabel="Gerar endereço novo"
                busy={ocupado}
                onConfirm={gerarNovaChave}
            >
                O endereço atual para de funcionar na hora. A ferramenta que envia os leads precisa receber o novo, senão os leads deixam de entrar.
            </Dialog>

            <Dialog
                open={confirmar === 'apagar'}
                onClose={() => setConfirmar(null)}
                title={`Apagar a entrada ${entrada?.nome ?? ''}?`}
                confirmLabel="Apagar"
                danger
                busy={ocupado}
                onConfirm={apagar}
            >
                O endereço para de funcionar e os leads dessa fonte deixam de entrar. Contatos e oportunidades que já entraram continuam no CRM.
            </Dialog>
        </>
    );
};

export default WebhookEntradaPanel;
